// src/pages/reseller/ResellerPartnerDetailPage.tsx

import {
  ArrowLeft,
  BriefcaseBusiness,
  CalendarDays,
  CheckCircle2,
  Clock,
  CreditCard,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Plus,
  RefreshCw,
  ShieldCheck,
  Trash2,
  User,
  Wallet,
  Wifi,
  X,
  XCircle,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../components/ui/Toast";
import { fetchApp } from "../../services/apiService";
import {
  activateResellerPartner,
  deactivateResellerPartner,
  generateResellerAccounts,
  getResellerPartnerDetail,
  recordResellerPayment,
  ResellerPartnerDetail,
} from "../reseller/resellerPartnersApi";

interface RouterOption {
  id: string;
  name: string;
  ip_address?: string | null;
}

interface PlanOption {
  id: string;
  name: string;
  speed?: string | null;
  price?: number | string | null;
  router_id?: string | null;
  router_name?: string | null;
}

interface GeneratePlanRow {
  plan_id: string;
  count: number;
  amount_payable: string;
}

type DetailAny = ResellerPartnerDetail & Record<string, any>;

const inputCls =
  "h-10 w-full rounded-2xl border border-slate-300 bg-slate-50 px-3.5 text-[13px] font-medium text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const textareaCls =
  "w-full resize-none rounded-2xl border border-slate-300 bg-slate-50 px-3.5 py-3 text-[13px] font-medium leading-relaxed text-slate-700 placeholder:text-slate-400 transition-all focus:border-emerald-400 focus:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-emerald-500/10";

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

function formatCurrency(value?: number | string | null) {
  if (value === null || value === undefined || value === "") return "KES 0";
  return `KES ${Number(value || 0).toLocaleString("en-KE")}`;
}

function formatDate(value?: string | null) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatStatus(value?: string | null) {
  return String(value || "UNKNOWN")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getStatusTone(status?: string | null) {
  const value = String(status || "").toLowerCase();

  if (value === "active") {
    return "border-emerald-200 bg-emerald-100 text-emerald-800";
  }

  if (value === "overdue") {
    return "border-amber-200 bg-amber-100 text-amber-800";
  }

  if (value === "suspended") {
    return "border-rose-200 bg-rose-100 text-rose-700";
  }

  if (value === "inactive") {
    return "border-slate-300 bg-slate-200 text-slate-700";
  }

  return "border-sky-200 bg-sky-100 text-sky-800";
}

function normalizeArray<T>(data: any): T[] {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.accounts)) return data.accounts;
  if (Array.isArray(data?.payments)) return data.payments;
  return [];
}

function getPartner(detail: DetailAny | null) {
  if (!detail) return null;
  return detail.partner || detail.reseller || detail.reseller_partner || detail;
}

function getAccounts(detail: DetailAny | null) {
  if (!detail) return [];
  return normalizeArray<any>(
    detail.accounts ||
      detail.pppoe_accounts ||
      detail.generated_accounts ||
      detail.inventory ||
      detail.secrets ||
      [],
  );
}

function getPayments(detail: DetailAny | null) {
  if (!detail) return [];
  return normalizeArray<any>(
    detail.payments ||
      detail.payment_history ||
      detail.transactions ||
      detail.partner_payments ||
      [],
  );
}

function valueFrom(...values: any[]) {
  return values.find(
    (item) => item !== undefined && item !== null && item !== "",
  );
}

const StatusBadge: React.FC<{ status?: string | null }> = ({ status }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-black ${getStatusTone(
      status,
    )}`}
  >
    <span className="h-1.5 w-1.5 rounded-full bg-current" />
    {formatStatus(status)}
  </span>
);

const ActiveBadge: React.FC<{
  active?: boolean;
  activeText?: string;
  inactiveText?: string;
}> = ({ active, activeText = "Active", inactiveText = "Inactive" }) => {
  if (active) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-1 text-[11px] font-black text-emerald-800">
        <CheckCircle2 className="h-3 w-3" />
        {activeText}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-rose-100 px-2.5 py-1 text-[11px] font-black text-rose-700">
      <XCircle className="h-3 w-3" />
      {inactiveText}
    </span>
  );
};

const LoadingState: React.FC = () => (
  <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
    <div className="mx-auto flex min-h-[60vh] max-w-[1600px] items-center justify-center">
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
        <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

        <div className="relative overflow-hidden px-6 py-10 text-center">
          <div className="absolute -right-16 -top-20 h-52 w-52 rounded-full bg-emerald-200/35 blur-2xl" />
          <div className="absolute -bottom-20 left-4 h-52 w-52 rounded-full bg-cyan-200/35 blur-2xl" />

          <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/50">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>

          <p className="relative mt-4 text-[15px] font-black text-slate-900">
            Loading reseller details…
          </p>

          <p className="relative mt-1 text-[12.5px] font-medium text-slate-500">
            Please wait while partner accounts, billing, and payments are being
            prepared.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "sky" | "amber" | "rose";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    sky: {
      card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
      icon: "bg-sky-100 text-sky-700",
      text: "text-sky-700",
      bubble: "bg-sky-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
    rose: {
      card: "border-rose-200 bg-gradient-to-br from-rose-50 to-red-50 shadow-rose-200/50",
      icon: "bg-rose-100 text-rose-700",
      text: "text-rose-700",
      bubble: "bg-rose-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-[22px] font-black tracking-tight text-slate-900 sm:text-3xl">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const InfoCard: React.FC<{
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, icon, children }) => (
  <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
    <div className="border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
          {icon}
        </div>

        <div>
          <p className="text-[14px] font-black text-slate-900">{title}</p>
          <p className="text-[11.5px] font-medium text-slate-500">{subtitle}</p>
        </div>
      </div>
    </div>

    <div className="p-4 sm:p-5">{children}</div>
  </div>
);

const DetailItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  tone?: "slate" | "emerald" | "sky" | "amber" | "rose";
}> = ({ icon, label, value, tone = "slate" }) => {
  const tones = {
    slate: "border-slate-200 bg-slate-100",
    emerald: "border-emerald-200 bg-emerald-50",
    sky: "border-sky-200 bg-sky-50",
    amber: "border-amber-200 bg-amber-50",
    rose: "border-rose-200 bg-rose-50",
  };

  return (
    <div className={`rounded-2xl border px-3 py-3 ${tones[tone]}`}>
      <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-500">
        <span className="text-slate-400">{icon}</span>
        {label}
      </div>

      <div className="mt-1 break-words text-[13px] font-black text-slate-900">
        {value || "—"}
      </div>
    </div>
  );
};

export default function ResellerPartnerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [detail, setDetail] = useState<DetailAny | null>(null);
  const [routers, setRouters] = useState<RouterOption[]>([]);
  const [plans, setPlans] = useState<PlanOption[]>([]);

  const [loading, setLoading] = useState(false);
  const [loadingRouters, setLoadingRouters] = useState(false);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [working, setWorking] = useState(false);

  const [showGenerate, setShowGenerate] = useState(false);
  const [showPayment, setShowPayment] = useState(false);

  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    payment_method: "Mpesa",
    payment_reference: "",
    notes: "",
  });

  const [generateForm, setGenerateForm] = useState<{
    router_id: string;
    username_prefix: string;
    plans: GeneratePlanRow[];
  }>({
    router_id: "",
    username_prefix: "",
    plans: [{ plan_id: "", count: 1, amount_payable: "" }],
  });

  const partner = getPartner(detail);
  const accounts = getAccounts(detail);
  const payments = getPayments(detail);

  const stats = useMemo(() => {
    const totalAccounts = Number(
      valueFrom(
        partner?.total_accounts,
        detail?.total_accounts,
        accounts.length,
        0,
      ),
    );

    const availableAccounts = Number(
      valueFrom(
        partner?.available_accounts,
        detail?.available_accounts,
        accounts.filter((item) => {
          const status = String(
            valueFrom(item.status, item.connection_status, ""),
          ).toLowerCase();

          return (
            status.includes("available") ||
            status.includes("unused") ||
            !item.customer_id
          );
        }).length,
        0,
      ),
    );

    const connectedAccounts = Number(
      valueFrom(
        partner?.connected_accounts,
        detail?.connected_accounts,
        accounts.filter((item) => {
          const status = String(
            valueFrom(item.status, item.connection_status, ""),
          ).toLowerCase();

          return (
            status.includes("connected") ||
            status.includes("active") ||
            Boolean(item.customer_id)
          );
        }).length,
        0,
      ),
    );

    const totalBill = Number(
      valueFrom(partner?.total_bill, detail?.total_bill, 0),
    );

    const totalPaid = Number(
      valueFrom(partner?.total_paid, detail?.total_paid, 0),
    );

    const balance = Number(
      valueFrom(partner?.balance, detail?.balance, totalBill - totalPaid),
    );

    return {
      totalAccounts,
      availableAccounts,
      connectedAccounts,
      totalBill,
      totalPaid,
      balance,
    };
  }, [partner, detail, accounts]);

  async function loadRouters(clientId?: string) {
    setLoadingRouters(true);

    try {
      const url = clientId
        ? `/routers/?client_id=${encodeURIComponent(clientId)}`
        : "/routers/";

      const routersRes = await fetchApp<any>(url);
      setRouters(normalizeArray<RouterOption>(routersRes));
    } catch (error: any) {
      showToast(error?.message || "Failed to load routers.", "error");
    } finally {
      setLoadingRouters(false);
    }
  }

  async function loadPlansByRouter(routerId: string) {
    if (!routerId) {
      setPlans([]);
      return;
    }

    setLoadingPlans(true);

    try {
      const plansRes = await fetchApp<any>(
        `/plans?router_id=${encodeURIComponent(routerId)}`,
      );

      setPlans(normalizeArray<PlanOption>(plansRes));
    } catch (error: any) {
      showToast(error?.message || "Failed to load plans.", "error");
    } finally {
      setLoadingPlans(false);
    }
  }

  async function loadDetail() {
    if (!id) return;

    setLoading(true);

    try {
      const data = (await getResellerPartnerDetail(id)) as DetailAny;

      setDetail(data);

      const loadedPartner = getPartner(data);

      setGenerateForm((prev) => ({
        ...prev,
        username_prefix: loadedPartner?.username_prefix || "",
      }));

      await loadRouters(loadedPartner?.client_id || data?.client_id);
    } catch (error: any) {
      showToast(error?.message || "Failed to load reseller partner.", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function handleRouterChange(routerId: string) {
    setGenerateForm((prev) => ({
      ...prev,
      router_id: routerId,
      plans: [{ plan_id: "", count: 1, amount_payable: "" }],
    }));

    await loadPlansByRouter(routerId);
  }

  function updatePlanRow(index: number, patch: Partial<GeneratePlanRow>) {
    setGenerateForm((prev) => ({
      ...prev,
      plans: prev.plans.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...patch } : row,
      ),
    }));
  }

  function addPlanRow() {
    setGenerateForm((prev) => ({
      ...prev,
      plans: [...prev.plans, { plan_id: "", count: 1, amount_payable: "" }],
    }));
  }

  function removePlanRow(index: number) {
    setGenerateForm((prev) => ({
      ...prev,
      plans:
        prev.plans.length === 1
          ? prev.plans
          : prev.plans.filter((_, rowIndex) => rowIndex !== index),
    }));
  }

  async function handleGenerateAccounts(e: React.FormEvent) {
    e.preventDefault();

    if (!id) return;

    if (!generateForm.router_id) {
      showToast("Please select a router.", "error");
      return;
    }

    const validPlans = generateForm.plans.filter(
      (row) => row.plan_id && Number(row.count) > 0,
    );

    if (validPlans.length === 0) {
      showToast("Select at least one plan and quantity.", "error");
      return;
    }

    try {
      setWorking(true);

      await generateResellerAccounts(id, {
        router_id: generateForm.router_id,
        username_prefix: generateForm.username_prefix.trim() || undefined,
        plans: validPlans.map((row) => ({
          plan_id: row.plan_id,
          count: Number(row.count),
          amount_payable: row.amount_payable
            ? Number(row.amount_payable)
            : undefined,
        })),
      });

      showToast("PPPoE accounts generated successfully.", "success");

      setShowGenerate(false);
      setPlans([]);
      setGenerateForm({
        router_id: "",
        username_prefix: partner?.username_prefix || "",
        plans: [{ plan_id: "", count: 1, amount_payable: "" }],
      });

      await loadDetail();
    } catch (error: any) {
      showToast(error?.message || "Failed to generate accounts.", "error");
    } finally {
      setWorking(false);
    }
  }

  async function handleRecordPayment(e: React.FormEvent) {
    e.preventDefault();

    if (!id) return;

    if (!paymentForm.amount || Number(paymentForm.amount) <= 0) {
      showToast("Enter a valid payment amount.", "error");
      return;
    }

    try {
      setWorking(true);

      await recordResellerPayment(id, {
        amount: Number(paymentForm.amount),
        payment_method: paymentForm.payment_method,
        payment_reference: paymentForm.payment_reference.trim() || undefined,
        notes: paymentForm.notes.trim() || undefined,
      });

      showToast("Payment recorded successfully.", "success");

      setShowPayment(false);
      setPaymentForm({
        amount: "",
        payment_method: "Mpesa",
        payment_reference: "",
        notes: "",
      });

      await loadDetail();
    } catch (error: any) {
      showToast(error?.message || "Failed to record payment.", "error");
    } finally {
      setWorking(false);
    }
  }

  async function handleDeactivate() {
    if (!id || !partner) return;

    const ok = window.confirm(
      `Deactivate ${partner.business_name}? Linked PPPoE accounts should be disabled by the backend.`,
    );

    if (!ok) return;

    try {
      setWorking(true);
      await deactivateResellerPartner(id);
      showToast("Reseller partner deactivated successfully.", "success");
      await loadDetail();
    } catch (error: any) {
      showToast(error?.message || "Failed to deactivate reseller.", "error");
    } finally {
      setWorking(false);
    }
  }

  async function handleActivate() {
    if (!id || !partner) return;

    const ok = window.confirm(`Activate ${partner.business_name}?`);

    if (!ok) return;

    try {
      setWorking(true);
      await activateResellerPartner(id);
      showToast("Reseller partner activated successfully.", "success");
      await loadDetail();
    } catch (error: any) {
      showToast(error?.message || "Failed to activate reseller.", "error");
    } finally {
      setWorking(false);
    }
  }

  if (loading && !detail) return <LoadingState />;

  if (!detail || !partner) {
    return (
      <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
        <div className="mx-auto max-w-[900px] rounded-3xl border border-rose-200 bg-rose-50 p-6 text-rose-700 shadow-sm">
          <p className="font-black">Reseller partner not found</p>

          <p className="mt-1 text-sm">
            The selected reseller partner could not be loaded.
          </p>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className={`${btnBase} mt-4 h-10 border border-rose-200 bg-white px-4 text-rose-700 hover:bg-rose-100`}
          >
            <ArrowLeft className="h-4 w-4" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const status = String(partner.status || "UNKNOWN").toUpperCase();
  const isActive = status === "ACTIVE";

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5 sm:space-y-6">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="mb-3 inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-[12px] font-black text-slate-600 shadow-sm transition hover:bg-slate-100 hover:text-slate-900"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Partners
                </button>

                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-emerald-700 sm:text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Reseller Partner Detail
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <h1 className="truncate text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[24px]">
                    {partner.business_name}
                  </h1>

                  <StatusBadge status={partner.status} />
                </div>

                <p className="mt-1 max-w-3xl text-[13px] font-medium text-slate-500">
                  {partner.contact_person || "No contact person"} •{" "}
                  {partner.phone_number || "No phone"} •{" "}
                  {partner.location || "No location"}
                </p>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={loadDetail}
                  disabled={loading || working}
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  Refresh
                </button>

                <button
                  type="button"
                  onClick={() => setShowPayment(true)}
                  className={`${btnBase} h-10 border border-sky-200 bg-sky-100 px-4 text-sky-800 hover:bg-sky-200`}
                >
                  <CreditCard className="h-4 w-4" />
                  Record Payment
                </button>

                <button
                  type="button"
                  onClick={() => setShowGenerate(true)}
                  className={`${btnBase} h-10 bg-gradient-to-r from-emerald-500 to-teal-500 px-5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
                >
                  <Plus className="h-4 w-4" />
                  Generate Accounts
                </button>

                {isActive ? (
                  <button
                    type="button"
                    onClick={handleDeactivate}
                    disabled={working}
                    className={`${btnBase} h-10 border border-rose-200 bg-rose-100 px-4 text-rose-700 hover:bg-rose-200`}
                  >
                    {working ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <XCircle className="h-4 w-4" />
                    )}
                    Deactivate
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleActivate}
                    disabled={working}
                    className={`${btnBase} h-10 border border-emerald-200 bg-emerald-100 px-4 text-emerald-800 hover:bg-emerald-200`}
                  >
                    {working ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4" />
                    )}
                    Activate
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {loading && detail && (
          <div className="flex items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-bold text-emerald-800 shadow-sm shadow-emerald-200/40">
            <Loader2 className="h-4 w-4 animate-spin" />
            Refreshing reseller partner details…
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5 xl:gap-4">
          <StatCard
            label="Total Accounts"
            value={stats.totalAccounts}
            sub={`${stats.connectedAccounts} connected`}
            icon={<Wifi className="h-5 w-5" />}
            tone="sky"
          />

          <StatCard
            label="Available"
            value={stats.availableAccounts}
            sub="Ready for customers"
            icon={<CheckCircle2 className="h-5 w-5" />}
            tone="emerald"
          />

          <StatCard
            label="Total Bill"
            value={formatCurrency(stats.totalBill)}
            sub="Current billing amount"
            icon={<Wallet className="h-5 w-5" />}
            tone="amber"
          />

          <StatCard
            label="Total Paid"
            value={formatCurrency(stats.totalPaid)}
            sub="Recorded payments"
            icon={<CreditCard className="h-5 w-5" />}
            tone="emerald"
          />

          <StatCard
            label="Balance"
            value={formatCurrency(stats.balance)}
            sub="Outstanding amount"
            icon={<Wallet className="h-5 w-5" />}
            tone={stats.balance > 0 ? "rose" : "emerald"}
          />
        </div>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
          <InfoCard
            title="Partner Profile"
            subtitle="Business identity and main contact information."
            icon={<BriefcaseBusiness className="h-5 w-5" />}
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <DetailItem
                icon={<BriefcaseBusiness className="h-3.5 w-3.5" />}
                label="Business Name"
                value={partner.business_name}
                tone="emerald"
              />

              <DetailItem
                icon={<User className="h-3.5 w-3.5" />}
                label="Contact Person"
                value={partner.contact_person}
              />

              <DetailItem
                icon={<Phone className="h-3.5 w-3.5" />}
                label="Phone Number"
                value={partner.phone_number}
                tone="sky"
              />

              <DetailItem
                icon={<Mail className="h-3.5 w-3.5" />}
                label="Email"
                value={partner.email}
              />

              <DetailItem
                icon={<MapPin className="h-3.5 w-3.5" />}
                label="Location"
                value={partner.location}
                tone="amber"
              />

              <DetailItem
                icon={<ShieldCheck className="h-3.5 w-3.5" />}
                label="Status"
                value={<StatusBadge status={partner.status} />}
                tone={isActive ? "emerald" : "rose"}
              />
            </div>
          </InfoCard>

          <InfoCard
            title="Billing Summary"
            subtitle="Billing expiry, grace period and payment status."
            icon={<Wallet className="h-5 w-5" />}
          >
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <DetailItem
                icon={<CreditCard className="h-3.5 w-3.5" />}
                label="Account Number"
                value={partner.account_number}
                tone="sky"
              />

              <DetailItem
                icon={<Wifi className="h-3.5 w-3.5" />}
                label="Username Prefix"
                value={partner.username_prefix}
              />

              <DetailItem
                icon={<CalendarDays className="h-3.5 w-3.5" />}
                label="Billing Started"
                value={formatDate(partner.billing_started_at)}
              />

              <DetailItem
                icon={<CalendarDays className="h-3.5 w-3.5" />}
                label="Billing Expires"
                value={formatDate(partner.billing_expires_at)}
                tone="amber"
              />

              <DetailItem
                icon={<Clock className="h-3.5 w-3.5" />}
                label="Payment Due"
                value={formatDate(partner.payment_due_at)}
                tone={stats.balance > 0 ? "rose" : "emerald"}
              />

              <DetailItem
                icon={<Clock className="h-3.5 w-3.5" />}
                label="Grace Ends"
                value={formatDate(partner.grace_ends_at)}
              />
            </div>
          </InfoCard>
        </div>

        <div className="space-y-5">
          <InfoCard
            title="PPPoE Account Inventory"
            subtitle="Generated reseller accounts, passwords, assignment and service status."
            icon={<Wifi className="h-5 w-5" />}
          >
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full min-w-[1200px] text-left text-[13px]">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-100 text-[11px] uppercase tracking-widest text-slate-500">
                    <th className="px-4 py-3 font-black">Username</th>
                    <th className="px-4 py-3 font-black">Password</th>
                    <th className="px-4 py-3 font-black">Plan</th>
                    <th className="px-4 py-3 font-black">Router</th>
                    <th className="px-4 py-3 font-black">Customer</th>
                    <th className="px-4 py-3 font-black">Amount</th>
                    <th className="px-4 py-3 font-black">Status</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-200 bg-slate-50">
                  {accounts.length > 0 ? (
                    accounts.map((account, index) => {
                      const accountStatus = valueFrom(
                        account.status,
                        account.connection_status,
                        account.secret_status,
                      );

                      const isAccountActive =
                        accountStatus === true ||
                        String(accountStatus || "").toLowerCase() ===
                          "active" ||
                        String(accountStatus || "").toLowerCase() ===
                          "connected";

                      const password = valueFrom(
                        account.password,
                        account.generated_password,
                        account.pppoe_password,
                        account.secret_password,
                        account.clear_password,
                        "—",
                      );

                      return (
                        <tr
                          key={account.id || account.username || index}
                          className="transition-colors hover:bg-emerald-50/60"
                        >
                          <td className="px-4 py-3">
                            <p className="font-mono text-[12.5px] font-black text-slate-900">
                              {valueFrom(
                                account.username,
                                account.secret_username,
                                "—",
                              )}
                            </p>

                            <p className="mt-0.5 text-[11px] font-medium text-slate-500">
                              {valueFrom(
                                account.account_number,
                                account.secret_account_number,
                                "",
                              ) || "PPPoE account"}
                            </p>
                          </td>

                          <td className="px-4 py-3">
                            <span className="inline-flex rounded-xl border border-amber-200 bg-amber-50 px-2.5 py-1 font-mono text-[12px] font-black text-amber-800">
                              {password}
                            </span>
                          </td>

                          <td className="px-4 py-3">
                            <p className="font-black text-slate-800">
                              {valueFrom(account.plan_name, account.plan, "—")}
                            </p>

                            <p className="mt-0.5 text-[11px] font-medium text-slate-500">
                              {valueFrom(account.plan_speed, account.speed, "")}
                            </p>
                          </td>

                          <td className="px-4 py-3 text-[12px] font-bold text-slate-700">
                            {valueFrom(
                              account.router_name,
                              account.router,
                              "—",
                            )}
                          </td>

                          <td className="px-4 py-3">
                            <p className="font-black text-slate-800">
                              {valueFrom(
                                account.customer_name,
                                account.full_name,
                                account.customer,
                                "Unassigned",
                              )}
                            </p>

                            <p className="mt-0.5 text-[11px] font-medium text-slate-500">
                              {valueFrom(
                                account.phone_number,
                                account.customer_phone,
                                "",
                              )}
                            </p>
                          </td>

                          <td className="px-4 py-3 font-black text-slate-900">
                            {formatCurrency(
                              valueFrom(
                                account.amount_payable,
                                account.price,
                                account.plan_price,
                                0,
                              ),
                            )}
                          </td>

                          <td className="px-4 py-3">
                            <ActiveBadge
                              active={isAccountActive}
                              activeText={String(accountStatus || "Active")}
                              inactiveText={String(accountStatus || "Inactive")}
                            />
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center">
                        <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                          <Wifi className="h-7 w-7 text-slate-500" />
                        </div>

                        <p className="text-[14px] font-black text-slate-800">
                          No PPPoE accounts yet
                        </p>

                        <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                          Generate reseller accounts from the button above.
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="divide-y divide-slate-200 md:hidden">
              {accounts.length > 0 ? (
                accounts.map((account, index) => {
                  const password = valueFrom(
                    account.password,
                    account.generated_password,
                    account.pppoe_password,
                    account.secret_password,
                    account.clear_password,
                    "—",
                  );

                  return (
                    <div
                      key={account.id || account.username || index}
                      className="py-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-mono text-[13px] font-black text-slate-900">
                            {valueFrom(
                              account.username,
                              account.secret_username,
                              "—",
                            )}
                          </p>

                          <p className="mt-1 text-[12px] font-medium text-slate-500">
                            {valueFrom(
                              account.plan_name,
                              account.plan,
                              "No plan",
                            )}
                          </p>
                        </div>

                        <span className="rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
                          {valueFrom(
                            account.router_name,
                            account.router,
                            "Router",
                          )}
                        </span>
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <DetailItem
                          icon={<Wifi className="h-3.5 w-3.5" />}
                          label="Password"
                          value={<span className="font-mono">{password}</span>}
                          tone="amber"
                        />

                        <DetailItem
                          icon={<User className="h-3.5 w-3.5" />}
                          label="Customer"
                          value={valueFrom(
                            account.customer_name,
                            account.full_name,
                            account.customer,
                            "Unassigned",
                          )}
                        />

                        <DetailItem
                          icon={<Wallet className="h-3.5 w-3.5" />}
                          label="Amount"
                          value={formatCurrency(
                            valueFrom(
                              account.amount_payable,
                              account.price,
                              account.plan_price,
                              0,
                            ),
                          )}
                          tone="emerald"
                        />

                        <DetailItem
                          icon={<ShieldCheck className="h-3.5 w-3.5" />}
                          label="Status"
                          value={String(
                            valueFrom(
                              account.status,
                              account.connection_status,
                              account.secret_status,
                              "Inactive",
                            ),
                          )}
                          tone="sky"
                        />
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="px-4 py-12 text-center">
                  <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                    <Wifi className="h-7 w-7 text-slate-500" />
                  </div>

                  <p className="text-[14px] font-black text-slate-800">
                    No PPPoE accounts yet
                  </p>
                </div>
              )}
            </div>
          </InfoCard>

          <InfoCard
            title="Payment History"
            subtitle="Recorded payments from this reseller partner."
            icon={<CreditCard className="h-5 w-5" />}
          >
            {payments.length > 0 ? (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {payments.map((payment, index) => (
                  <div
                    key={payment.id || index}
                    className="rounded-3xl border border-slate-200 bg-slate-100 px-4 py-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[15px] font-black text-slate-900">
                          {formatCurrency(
                            valueFrom(payment.amount, payment.paid_amount, 0),
                          )}
                        </p>

                        <p className="mt-1 text-[12px] font-bold text-slate-500">
                          {valueFrom(
                            payment.payment_method,
                            payment.method,
                            "Payment",
                          )}
                        </p>
                      </div>

                      <span className="rounded-full border border-emerald-200 bg-emerald-100 px-2.5 py-1 text-[11px] font-black text-emerald-800">
                        Paid
                      </span>
                    </div>

                    <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <DetailItem
                        icon={<CreditCard className="h-3.5 w-3.5" />}
                        label="Reference"
                        value={valueFrom(
                          payment.payment_reference,
                          payment.reference,
                          payment.ref,
                          "—",
                        )}
                      />

                      <DetailItem
                        icon={<CalendarDays className="h-3.5 w-3.5" />}
                        label="Date"
                        value={formatDate(
                          valueFrom(
                            payment.created_at,
                            payment.paid_at,
                            payment.payment_date,
                          ),
                        )}
                      />
                    </div>

                    {valueFrom(payment.notes, payment.description, "") && (
                      <p className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 text-[12.5px] font-medium text-slate-600">
                        {valueFrom(payment.notes, payment.description, "")}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-4 py-12 text-center">
                <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                  <CreditCard className="h-7 w-7 text-slate-500" />
                </div>

                <p className="text-[14px] font-black text-slate-800">
                  No payments recorded
                </p>

                <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                  Recorded reseller payments will appear here.
                </p>
              </div>
            )}
          </InfoCard>
        </div>
      </div>

      {showGenerate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-[2px]">
          <div className="max-h-[92vh] w-full max-w-4xl overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-100/90 px-5 py-4">
              <div>
                <p className="text-[15px] font-black text-slate-900">
                  Generate PPPoE Accounts
                </p>

                <p className="mt-1 text-[12.5px] font-medium text-slate-500">
                  Select a router, then generate account inventory per plan.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowGenerate(false)}
                disabled={working}
                className="rounded-2xl p-2 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form
              onSubmit={handleGenerateAccounts}
              className="max-h-[calc(92vh-80px)] overflow-y-auto p-5"
            >
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Router
                  </label>

                  <select
                    className={`${inputCls} cursor-pointer`}
                    value={generateForm.router_id}
                    onChange={(e) => handleRouterChange(e.target.value)}
                  >
                    <option value="">
                      {loadingRouters
                        ? "Loading routers..."
                        : "Select router..."}
                    </option>

                    {routers.map((router) => (
                      <option key={router.id} value={router.id}>
                        {router.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Username Prefix
                  </label>

                  <input
                    className={inputCls}
                    value={generateForm.username_prefix}
                    onChange={(e) =>
                      setGenerateForm((prev) => ({
                        ...prev,
                        username_prefix: e.target.value,
                      }))
                    }
                    placeholder="e.g. QUANTUM"
                  />
                </div>
              </div>

              <div className="mt-5 rounded-3xl border border-slate-200 bg-slate-100/80 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-[13px] font-black text-slate-900">
                      Plan Quantities
                    </p>

                    <p className="text-[11.5px] font-medium text-slate-500">
                      Add how many accounts to generate for each plan.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={addPlanRow}
                    className={`${btnBase} h-9 border border-emerald-200 bg-emerald-100 px-3 text-emerald-800 hover:bg-emerald-200`}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add Plan
                  </button>
                </div>

                <div className="mt-4 space-y-3">
                  {generateForm.plans.map((row, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 gap-3 rounded-3xl border border-slate-200 bg-slate-50 p-3 md:grid-cols-[1fr_120px_160px_42px]"
                    >
                      <select
                        className={`${inputCls} cursor-pointer`}
                        value={row.plan_id}
                        onChange={(e) =>
                          updatePlanRow(index, { plan_id: e.target.value })
                        }
                        disabled={!generateForm.router_id || loadingPlans}
                      >
                        <option value="">
                          {loadingPlans ? "Loading plans..." : "Select plan..."}
                        </option>

                        {plans.map((plan) => (
                          <option key={plan.id} value={plan.id}>
                            {plan.name}
                            {plan.speed ? ` • ${plan.speed}` : ""}
                            {plan.price
                              ? ` • ${formatCurrency(plan.price)}`
                              : ""}
                          </option>
                        ))}
                      </select>

                      <input
                        className={inputCls}
                        type="number"
                        min={1}
                        value={row.count}
                        onChange={(e) =>
                          updatePlanRow(index, {
                            count: Number(e.target.value || 1),
                          })
                        }
                        placeholder="Count"
                      />

                      <input
                        className={inputCls}
                        type="number"
                        min={0}
                        value={row.amount_payable}
                        onChange={(e) =>
                          updatePlanRow(index, {
                            amount_payable: e.target.value,
                          })
                        }
                        placeholder="Amount payable"
                      />

                      <button
                        type="button"
                        onClick={() => removePlanRow(index)}
                        disabled={generateForm.plans.length === 1}
                        className="flex h-10 items-center justify-center rounded-2xl border border-rose-200 bg-rose-100 text-rose-700 hover:bg-rose-200 disabled:opacity-40"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-5 flex flex-col-reverse gap-2 border-t border-slate-200 pt-4 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setShowGenerate(false)}
                  disabled={working}
                  className={`${btnBase} h-11 border border-slate-300 bg-slate-50 px-5 text-slate-700 hover:bg-slate-100`}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={working}
                  className={`${btnBase} h-11 bg-gradient-to-r from-emerald-500 to-teal-500 px-6 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
                >
                  {working ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  {working ? "Generating..." : "Generate Accounts"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-[2px]">
          <div className="w-full max-w-xl overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-100/90 px-5 py-4">
              <div>
                <p className="text-[15px] font-black text-slate-900">
                  Record Payment
                </p>

                <p className="mt-1 text-[12.5px] font-medium text-slate-500">
                  Add payment made by this reseller partner.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowPayment(false)}
                disabled={working}
                className="rounded-2xl p-2 text-slate-500 hover:bg-slate-200 hover:text-slate-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleRecordPayment} className="space-y-4 p-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Amount
                  </label>

                  <input
                    className={inputCls}
                    type="number"
                    min={1}
                    value={paymentForm.amount}
                    onChange={(e) =>
                      setPaymentForm((prev) => ({
                        ...prev,
                        amount: e.target.value,
                      }))
                    }
                    placeholder="Enter amount"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                    Payment Method
                  </label>

                  <select
                    className={`${inputCls} cursor-pointer`}
                    value={paymentForm.payment_method}
                    onChange={(e) =>
                      setPaymentForm((prev) => ({
                        ...prev,
                        payment_method: e.target.value,
                      }))
                    }
                  >
                    <option value="Mpesa">Mpesa</option>
                    <option value="Cash">Cash</option>
                    <option value="Bank">Bank</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                  Payment Reference
                </label>

                <input
                  className={inputCls}
                  value={paymentForm.payment_reference}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({
                      ...prev,
                      payment_reference: e.target.value,
                    }))
                  }
                  placeholder="Mpesa code / bank ref / receipt number"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-[11px] font-black uppercase tracking-widest text-slate-500">
                  Notes
                </label>

                <textarea
                  className={textareaCls}
                  rows={4}
                  value={paymentForm.notes}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  placeholder="Optional payment notes..."
                />
              </div>

              <div className="flex flex-col-reverse gap-2 border-t border-slate-200 pt-4 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setShowPayment(false)}
                  disabled={working}
                  className={`${btnBase} h-11 border border-slate-300 bg-slate-50 px-5 text-slate-700 hover:bg-slate-100`}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={working}
                  className={`${btnBase} h-11 bg-gradient-to-r from-emerald-500 to-teal-500 px-6 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
                >
                  {working ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CreditCard className="h-4 w-4" />
                  )}
                  {working ? "Saving..." : "Record Payment"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
